import React from 'react';
import { IProduct } from '../../Api/Models/Products';
import { useLocalization } from '../Helpers/useLocalization';

// Above this, the remaining quantity is not worth stating: "500 available" is noise, and the previous
// blanket notice was ignored precisely because it appeared on everything.
// A display heuristic, not a business rule, which is why it lives here and not in the BFF. If merchants
// want it per storefront it should graduate into storefront settings.
export const LOW_STOCK_THRESHOLD = 10;

interface IStockAvailabilityNoticeProps {
    product?: IProduct;
    className?: string;
    style?: React.CSSProperties;
}

// AC-987: tells the shopper up front that stock is short, instead of only finding out by typing too large
// a quantity and being corrected. Replaces the old blanket "Limited supply at this price", which fired on
// essentially every product (a single in-stock vendor is enough to produce a price level) and never named
// a number.
//
// Worded as a plain count rather than "Only N available": the point is to state what is purchasable, not
// to editorialize about scarcity.
//
// availableQuantity is null unless the product is genuinely capped: the BFF resolves the AlwaysAvailable
// exemption, since those products carry a MaxQuantity they are deliberately never held to.
const StockAvailabilityNotice: React.FC<IStockAvailabilityNoticeProps> = ({ product, className, style }) => {
    const L = useLocalization();
    const available = product?.availableQuantity;

    if (available === undefined || available === null) return null;
    if (available <= 0 || available > LOW_STOCK_THRESHOLD) return null;

    // Global replace: the copy is merchant-editable, so {quantity} may legitimately appear more than once.
    const text = L('localization_stock_low').replace(/\{quantity\}/g, available.toLocaleString());

    // A block-displayed span rather than <p>: this is dropped into whatever container a surface happens
    // to have, and a span is valid in both flow and phrasing contexts. It also carries no UA margin, so
    // callers stop having to zero one out.
    return (
        <span className={className} style={{ display: 'block', fontStyle: 'italic', ...style }}>{text}</span>
    );
};

export default StockAvailabilityNotice;
